import { ContainerBloco, LinhaInfo, TituloBloco, TextoAvulso } from "../../../Common/styles";
import { IProposta } from "../../../../../utility/IProposta";
import { IconClock } from "../../../../icons";
import { useMemo, useCallback, useState, useEffect } from "react";
import { FileText, Scale, Building2 } from "lucide-react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { BrasiliaInfoBasica } from "./styles";
import SelectEditavel from "../../../../form/SelectEditavel";
import SelectEstadoEditavel from "../../../../form/SelectEstadoEditavel";
import SelectComarcaEditavel from "../../../../form/SelectComarcaEditavel";
import DateTimePickerEditavel from "../../../../form/DateTimePickerEditavel";
import api from "../../../../../services/api";
import { mapearTituloBancoParaExibicao } from "../../../../../utils/servicoMapper";
import toast from "react-hot-toast";
import { getFusoHorarioPorEstado, getFusoHorarioPorComarca } from "../../../../../utility/timezoneUtils";

interface BlocoInfoBasicaProps {
    demanda?: IProposta | null;
    loading?: boolean;
    isEditing?: boolean;
    isDisabled?: boolean;
    onChange?: (campo: string, valor: any) => void;
    onBlur?: (campo: string) => void;
    errors?: Record<string, string>;
}

const getAreaDireitoOptions = (tipoDemanda: string | undefined): { value: string; label: string }[] => {
    if (!tipoDemanda) {
        return [];
    }

    const tipoNormalizado = tipoDemanda.toLowerCase().trim();
    
    if (tipoNormalizado === 'audiências' || tipoNormalizado === 'audiencia' || tipoNormalizado === 'audiencias') {
        return [
            { value: 'Audiência Cível', label: 'Audiência Cível' },
            { value: 'Audiência Penal', label: 'Audiência Penal' },
            { value: 'Audiência Trabalhista', label: 'Audiência Trabalhista' },
            { value: 'Outras Audiências', label: 'Outras Audiências' }
        ];
    }
    
    if (tipoNormalizado === 'diligências' || tipoNormalizado === 'diligencia' || tipoNormalizado === 'diligencias') {
        return [
            { value: 'Acompanhamento de atos oficiais', label: 'Acompanhamento de atos oficiais' },
            { value: 'Comparecimento em cartório', label: 'Comparecimento em cartório' },
            { value: 'Confirmação de endereço', label: 'Confirmação de endereço' },
            { value: 'Cópia integral', label: 'Cópia integral' },
            { value: 'Cópia parcial', label: 'Cópia parcial' },
            { value: 'Despacho', label: 'Despacho' },
            { value: 'Emissão de guia', label: 'Emissão de guia' },
            { value: 'Outras Diligências', label: 'Outras Diligências' },
            { value: 'Penal', label: 'Penal' },
            { value: 'Retirada de ofício e alvará', label: 'Retirada de ofício e alvará' }
        ];
    }
    
    if (tipoNormalizado === 'petições' || tipoNormalizado === 'petição' || tipoNormalizado === 'peticoes') {
        return [
            { value: 'Alta Complexidade', label: 'Alta Complexidade' },
            { value: 'Baixa Complexidade', label: 'Baixa Complexidade' },
            { value: 'Cálculo Simples', label: 'Cálculo Simples' },
            { value: 'Contestação', label: 'Contestação' },
            { value: 'Estratégia Processual Simples', label: 'Estratégia Processual Simples' },
            { value: 'Inicial', label: 'Inicial' },
            { value: 'Outras Petições', label: 'Outras Petições' },
            { value: 'Penal', label: 'Penal' },
            { value: 'Recursos', label: 'Recursos' },
            { value: 'Requerimento Administrativo', label: 'Requerimento Administrativo' }
        ];
    }

    if (tipoNormalizado === 'protocolos' || tipoNormalizado === 'protocolo') {
        return [
            { value: 'Acautelamento', label: 'Acautelamento' },
            { value: 'Penal', label: 'Penal' },
            { value: 'Protocolo eletrônico', label: 'Protocolo eletrônico' },
            { value: 'Protocolo físico', label: 'Protocolo físico' },
            { value: 'Protocolo postal', label: 'Protocolo postal' }
        ];
    }
    
    if (tipoNormalizado === 'outros' || tipoNormalizado === 'outro') {
        return [
            { value: 'Localização de Veículo', label: 'Localização de Veículo' },
            { value: 'Outro serviço', label: 'Outro serviço' }
        ];
    }
    
    return [];
};


export default function BlocoInfoBasica({demanda, loading = false, isEditing = false, isDisabled = false, onChange, onBlur, errors}: BlocoInfoBasicaProps) {
    const [servicosCache, setServicosCache] = useState<Array<{id_servico: number, titulo: string, id_categoria_de_servico: number}>>([]);

    useEffect(() => {
        const carregarServicos = async () => {
            try {
                const response = await api.get('servico');
                if (response.data?.status === 'success' && response.data?.result) {
                    setServicosCache(response.data.result);
                }
            } catch (error) {
                console.error('Erro ao carregar serviços:', error);
            }
        };
        if (isEditing) {
            carregarServicos();
        }
    }, [isEditing]);

    const valorAreaDireitoAtual = useMemo(() => {
        const tituloMapeado = mapearTituloBancoParaExibicao(demanda?.titulo_servico);
        const tituloOriginal = (demanda?.titulo_servico || '').trim();
        if (tituloOriginal === 'Outros' && demanda?.descricao_categoria_servico === 'Outros') return 'Outro serviço';
        return (tituloMapeado || tituloOriginal || '').trim();
    }, [demanda?.titulo_servico, demanda?.descricao_categoria_servico]);

    const areaDireitoOptions = useMemo(() => {
        const opcoes = getAreaDireitoOptions(demanda?.descricao_categoria_servico);
        const normalizar = (s: string) => (s || '').toLowerCase().trim();
        if (valorAreaDireitoAtual && !opcoes.some(o => 
            o.value === valorAreaDireitoAtual || 
            o.label === valorAreaDireitoAtual || 
            normalizar(o.value) === normalizar(valorAreaDireitoAtual) ||
            normalizar(o.label) === normalizar(valorAreaDireitoAtual)
        )) {
            return [{ value: valorAreaDireitoAtual, label: valorAreaDireitoAtual }, ...opcoes];
        }
        return opcoes;
    }, [demanda?.descricao_categoria_servico, valorAreaDireitoAtual]);

    const tituloServicoParaExibicao = useMemo(() => {
        if (!valorAreaDireitoAtual) return undefined;
        const opcoes = areaDireitoOptions;
        const normalizar = (s: string) => (s || '').toLowerCase().trim();
        const encontrado = opcoes.find(o => 
            o.value === valorAreaDireitoAtual || 
            o.label === valorAreaDireitoAtual ||
            normalizar(o.value) === normalizar(valorAreaDireitoAtual) ||
            normalizar(o.label) === normalizar(valorAreaDireitoAtual)
        );
        return encontrado ? encontrado.value : valorAreaDireitoAtual;
    }, [valorAreaDireitoAtual, areaDireitoOptions]);

    const timezoneInfo = useMemo(() => {
        const timezoneMap = {
            'BRT': { name: 'Brasília', utc: 'UTC-3' },
            'AMT': { name: 'Amazonas', utc: 'UTC-4' },
            'ACT': { name: 'Acre', utc: 'UTC-5' },
            'FNT': { name: 'Fernando de Noronha', utc: 'UTC-2' }
        };
        
        return timezoneMap[demanda?.data_tz as keyof typeof timezoneMap] || { name: 'Desconhecido', utc: 'UTC-3' };
    }, [demanda?.data_tz]);

    const formattedDateTime = useMemo(() => {
        if (!demanda?.data) return 'N/A';
        
        const date = new Date(demanda.data);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        
        return `${day}/${month}/${year} às ${hours}h${minutes}`;
    }, [demanda?.data]);

    const brasiliaDateTime = useMemo(() => {
        if (!demanda?.data || !demanda?.data_tz) return 'N/A';
        
        const originalDate = new Date(demanda.data);
        let brasiliaDate = new Date(originalDate);
        
        // Convert to Brasília time based on original timezone
        const timezoneOffsets = {
            'BRT': 0,    // Already in BRT
            'AMT': 1,    // AMT is UTC-4, BRT is UTC-3, so add 1 hour
            'ACT': 2,    // ACT is UTC-5, BRT is UTC-3, so add 2 hours
            'FNT': -1    // FNT is UTC-2, BRT is UTC-3, so subtract 1 hour
        };
        
        const offset = timezoneOffsets[demanda.data_tz as keyof typeof timezoneOffsets] || 0;
        brasiliaDate.setHours(brasiliaDate.getHours() + offset);
        
        const day = String(brasiliaDate.getDate()).padStart(2, '0');
        const month = String(brasiliaDate.getMonth() + 1).padStart(2, '0');
        const year = brasiliaDate.getFullYear();
        const hours = String(brasiliaDate.getHours()).padStart(2, '0');
        const minutes = String(brasiliaDate.getMinutes()).padStart(2, '0');
        
        return `${day}/${month}/${year} às ${hours}h${minutes}`;
    }, [demanda?.data, demanda?.data_tz]);

    const getModalidadeFlags = useCallback((modalidade: string | undefined) => {
        const isPresencial = modalidade === 'presencial' || modalidade === 'Presencial';
        const isVirtual = modalidade === 'Virtual' || modalidade === 'online';
        return { isPresencial, isVirtual };
    }, []);

    const mapearTituloParaBanco = useCallback((value: string, categoria: string | undefined, modalidade: string | undefined): string => {
        if (categoria !== 'Audiências') return value;
        
        const { isPresencial, isVirtual } = getModalidadeFlags(modalidade);
        const mapeamentoTitulo: Record<string, { presencial: string, virtual: string }> = {
            'Audiência Cível': { presencial: 'Cível Presencial', virtual: 'Cível Virtual' },
            'Audiência Trabalhista': { presencial: 'Trabalhista Presencial', virtual: 'Trabalhista Virtual' },
            'Audiência Penal': { presencial: 'Penal Presencial', virtual: 'Penal Virtual' },
            'Outras Audiências': { presencial: 'Outros Presencial', virtual: 'Outros Virtual' }
        };
        
        const mapeamento = mapeamentoTitulo[value];
        return mapeamento ? (isPresencial ? mapeamento.presencial : (isVirtual ? mapeamento.virtual : mapeamento.presencial)) : value;
    }, [getModalidadeFlags]);

    const getIdCategoriaFromDescricao = useCallback((descricao: string | undefined): number | null => {
        if (!descricao) return null;
        const d = descricao.toLowerCase().trim();
        if (d === 'audiências' || d === 'audiencia' || d === 'audiencias') return 1;
        if (d === 'diligências' || d === 'diligencia' || d === 'diligencias') return 2;
        if (d === 'protocolos' || d === 'protocolo') return 3;
        if (d === 'outros' || d === 'outro') return 4;
        if (d === 'petição' || d === 'petições' || d === 'peticoes' || d === 'peticao') return 5;
        return null;
    }, []);

    const buscarIdServico = useCallback(async (value: string, categoria: string | undefined, modalidade: string | undefined): Promise<number | null> => {
        const { isPresencial, isVirtual } = getModalidadeFlags(modalidade);
        const idCategoria = getIdCategoriaFromDescricao(categoria);

        if (categoria === 'Audiências') {
            const mapeamentoAudiencia: Record<string, { presencial: number, virtual: number }> = {
                'Audiência Cível': { presencial: 1, virtual: 17 },
                'Audiência Trabalhista': { presencial: 2, virtual: 18 },
                'Audiência Penal': { presencial: 3, virtual: 19 },
                'Outras Audiências': { presencial: 4, virtual: 20 }
            };
            const mapeamento = mapeamentoAudiencia[value];
            if (mapeamento) {
                return isPresencial ? mapeamento.presencial : (isVirtual ? mapeamento.virtual : mapeamento.presencial);
            }
        } else {
            const mapeamentoDiligencia: Record<string, number> = {
                'Acompanhamento de atos oficiais': 5,
                'Comparecimento em cartório': 6,
                'Cópia integral': 8,
                'Cópia parcial': 9,
                'Despacho': 10,
                'Retirada de ofício e alvará': 11,
                'Petições': 22,
                'Outras Diligências': 12
            };
            const mapeamentoProtocolo: Record<string, number> = {
                'Acautelamento': 13,
                'Protocolo eletrônico': 14,
                'Protocolo físico': 15,
                'Protocolo postal': 16
            };
            const mapeamentoOutros: Record<string, number> = {
                'Outro serviço': 21,
                'Localização de Veículo': 23
            };
            const idPorCategoria = idCategoria === 2 ? mapeamentoDiligencia[value]
                : idCategoria === 3 ? mapeamentoProtocolo[value]
                : idCategoria === 4 ? mapeamentoOutros[value]
                : undefined;
            if (idPorCategoria != null) return idPorCategoria;
            if (idCategoria !== 5) {
                const idServico = mapeamentoDiligencia[value] || mapeamentoProtocolo[value] || mapeamentoOutros[value];
                if (idServico) return idServico;
            }
        }

        if (servicosCache.length > 0) {
            const normalizar = (s: string) => (s || '').toLowerCase().trim();
            const candidatos = idCategoria != null
                ? servicosCache.filter(s => normalizar(s.titulo) === normalizar(value) && s.id_categoria_de_servico === idCategoria)
                : servicosCache.filter(s => normalizar(s.titulo) === normalizar(value));
            const servicoEncontrado = candidatos[0];
            if (servicoEncontrado) return servicoEncontrado.id_servico;
        }

        try {
            const params = new URLSearchParams({ titulo: value });
            if (idCategoria != null) params.set('id_categoria_de_servico', String(idCategoria));
            const response = await api.get(`servico?${params.toString()}`);
            if (response.data?.status === 'success' && response.data?.result?.length > 0) {
                return response.data.result[0].id_servico;
            }
        } catch (error) {
            console.error('Erro ao buscar serviço:', error);
        }

        return null;
    }, [servicosCache, getModalidadeFlags, getIdCategoriaFromDescricao]);

    const handleAreaDireitoChange = useCallback(async (value: string) => {
        if (!onChange) return;
        
        const categoria = demanda?.descricao_categoria_servico;
        const modalidade = demanda?.modalidade;
        
        const tituloParaSalvar = mapearTituloParaBanco(value, categoria, modalidade);
        onChange('titulo_servico', tituloParaSalvar);
        
        if (value) {
            const idServico = await buscarIdServico(value, categoria, modalidade);
            if (idServico) {
                onChange('id_servico', idServico);
            }
        }
    }, [onChange, demanda?.descricao_categoria_servico, demanda?.modalidade, mapearTituloParaBanco, buscarIdServico]);

    const handleEstadoChange = useCallback((value: string) => {
        if (onChange) {
            onChange('uf_comarca', value);
            if (value !== demanda?.uf_comarca) {
                onChange('id_comarca', null);
                onChange('cidade_comarca', '');
            }
            if (isEditing) {
                const fusoDetectado = getFusoHorarioPorEstado(value);
                if (fusoDetectado && fusoDetectado !== demanda?.data_tz) {
                    onChange('data_tz', fusoDetectado);
                    toast.dismiss();
                    toast.custom(
                        (t) => (
                            <div style={{ background: 'var(--cffffff)', border: '0.5px solid #EAEEF3', borderRadius: 8, padding: 16, minWidth: 300, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                                <div style={{ marginBottom: 12 }}>
                                    <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--ce52940)' }}>Fuso padrão diferente</div>
                                    <div style={{ fontSize: 14, color: 'var(--c3e4b52)' }}>O fuso horário padrão desta região é {fusoDetectado}.</div>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <button 
                                        onClick={() => toast.dismiss(t.id)} 
                                        style={{ 
                                            padding: '6px 12px', 
                                            border: '1px solid #E52940', 
                                            borderRadius: 6, 
                                            background: 'var(--ce52940)', 
                                            color: 'var(--ink-white)', 
                                            cursor: 'pointer',
                                            fontSize: 14,
                                            fontWeight: 500,
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        OK
                                    </button>
                                </div>
                            </div>
                        ),
                        { duration: 15000 }
                    );
                }
            }
        }
    }, [onChange, demanda?.uf_comarca, demanda?.data_tz, isEditing]);

    const handleComarcaChange = useCallback(async (idComarca: string, cidadeComarca?: string) => {
        if (onChange) {
            onChange('id_comarca', idComarca ? Number(idComarca) : null);
            if (cidadeComarca) {
                onChange('cidade_comarca', cidadeComarca);
            }
            
            if (isEditing && idComarca) {
                try {
                    const response = await api.get(`comarca/${idComarca}`);
                    if (response.data && response.data.status === 'success' && response.data.result) {
                        const comarca = response.data.result;
                        const fusoDetectado = getFusoHorarioPorComarca(comarca);
                        
                        if (fusoDetectado && fusoDetectado !== demanda?.data_tz) {
                            onChange('data_tz', fusoDetectado);
                            toast.dismiss();
                            toast.custom(
                                (t) => (
                                    <div style={{ background: 'var(--cffffff)', border: '0.5px solid #EAEEF3', borderRadius: 8, padding: 16, minWidth: 300, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                                        <div style={{ marginBottom: 12 }}>
                                            <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--ce52940)' }}>Fuso padrão diferente</div>
                                            <div style={{ fontSize: 14, color: 'var(--c3e4b52)' }}>O fuso horário padrão desta comarca é {fusoDetectado}.</div>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <button 
                                                onClick={() => toast.dismiss(t.id)} 
                                                style={{ 
                                                    padding: '6px 12px', 
                                                    border: '1px solid #E52940', 
                                                    borderRadius: 6, 
                                                    background: 'var(--ce52940)', 
                                                    color: 'var(--ink-white)', 
                                                    cursor: 'pointer',
                                                    fontSize: 14,
                                                    fontWeight: 500,
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                OK
                                            </button>
                                        </div>
                                    </div>
                                ),
                                { duration: 15000 }
                            );
                        }
                    }
                } catch (error) {
                    console.error('Erro ao buscar fuso horário da comarca:', error);
                }
            }
        }
    }, [onChange, demanda?.data_tz, isEditing]);

    const handleDataChange = (value: string) => {
        if (onChange) {
            onChange('data', value);
        }
    };

    return (
        <ContainerBloco>
            {loading ? (
                <>
                    <Skeleton height={24} width="60%" style={{ marginBottom: '12px' }} />
                    <Skeleton height={20} width="100%" style={{ marginBottom: '12px' }} />
                    <Skeleton height={20} width="100%" style={{ marginBottom: '12px' }} />
                    <Skeleton height={20} width="100%" style={{ marginBottom: '12px' }} />
                    <Skeleton height={16} width="80%" style={{ marginBottom: '8px' }} />
                    <Skeleton height={16} width="80%" />
                </>
            ) : (
                <>
                <TituloBloco>Informações Básicas</TituloBloco>
                <>
                    <LinhaInfo>
                        <span className="label">
                            <FileText size={16} color="#8A99A8"/>
                            Tipo de Demanda:
                        </span>
                        <span className="value">
                            {demanda?.descricao_categoria_servico || 'N/A'}
                        </span>
                    </LinhaInfo>
                    <LinhaInfo>
                        <span className="label">
                            <Scale size={16} color="#8A99A8"/>
                            Área do Direito:
                        </span>
                        <SelectEditavel
                            value={tituloServicoParaExibicao}
                            onChange={handleAreaDireitoChange}
                            onBlur={() => onBlur && onBlur('titulo_servico')}
                            isEditing={isEditing}
                            disabled={isDisabled}
                            options={areaDireitoOptions}
                            placeholder="Selecionar"
                            error={errors?.titulo_servico}
                        />
                    </LinhaInfo>
                    {isEditing ? (
                        <>
                            <LinhaInfo>
                                <span className="label">
                                    <Building2 size={16} color="#8A99A8"/>
                                    Estado:
                                </span>
                                <SelectEstadoEditavel
                                    value={demanda?.uf_comarca}
                                    onChange={handleEstadoChange}
                                    onBlur={() => onBlur && onBlur('uf_comarca')}
                                    isEditing={isEditing}
                                    disabled={isDisabled}
                                    placeholder="Estado"
                                    error={errors?.uf_comarca}
                                />
                            </LinhaInfo>
                            <LinhaInfo>
                                <span className="label">
                                    <Building2 size={16} color="#8A99A8"/>
                                    Comarca:
                                </span>
                                <SelectComarcaEditavel
                                    estado={demanda?.uf_comarca}
                                    value={demanda?.id_comarca}
                                    onChange={handleComarcaChange}
                                    onBlur={() => onBlur && onBlur('id_comarca')}
                                    isEditing={isEditing}
                                    disabled={isDisabled || !demanda?.uf_comarca}
                                    placeholder="Comarca"
                                    error={errors?.id_comarca}
                                />
                            </LinhaInfo>
                        </>
                    ) : (
                        <LinhaInfo>
                            <span className="label">
                                <Building2 size={16} color="#8A99A8"/>
                                Estado/Comarca:
                            </span>
                            <span className="value">
                                {(demanda?.uf_comarca && demanda?.cidade_comarca) ? `${demanda.uf_comarca} - ${demanda.cidade_comarca}` : 'N/A'}
                            </span>
                        </LinhaInfo>
                    )}
                    <LinhaInfo>
                        <span className="label">
                            <IconClock size={16}/> Prazo Fatal:
                        </span>
                        <DateTimePickerEditavel
                            value={demanda?.data}
                            onChange={handleDataChange}
                            onBlur={() => onBlur && onBlur('data')}
                            isEditing={isEditing}
                            disabled={isDisabled}
                            timezone={demanda?.data_tz ? `${demanda.data_tz} (${timezoneInfo.utc})` : undefined}
                            error={errors?.data}
                        />
                    </LinhaInfo>
                    {!isEditing && (
                        <BrasiliaInfoBasica>
                            <TextoAvulso>
                                Horário Local: {timezoneInfo.name}
                            </TextoAvulso>
                            {demanda?.data_tz !== 'BRT' && (
                                <TextoAvulso>
                                    Brasília: {brasiliaDateTime} (BRT)
                                </TextoAvulso>
                            )}
                        </BrasiliaInfoBasica>
                    )}
                    </>
                </>
            )}
        </ContainerBloco>
    )
}